import { useCallback } from 'react'
import { useReCaptcha } from '@utils/hooks'
import { useSnackBar } from '@utils/snackbar'
import type { ForceAny } from '@utils/typescript'
import { useLeadGeneration } from 'src/client-query/lead'

export const useOddoIntegrationWithCalendly = () => {
  const { captchaToken } = useReCaptcha()
  const { show } = useSnackBar()

  // Lead generation hook
  const { createSoftLead } = useLeadGeneration({
    onSuccess: () => {
      show.success({
        title:
          "Meeting scheduled successfully! We'll send you a confirmation email shortly.",
      })
    },
    onFailure: () => {
      show.error({
        title:
          'Meeting scheduled, but there was an issue saving your information. Please contact us directly.',
      })
    },
  })

  const handleEventSchedule = useCallback(
    async (e: ForceAny) => {
      try {
        const eventUri = e.data.payload.event.uri
        const inviteeUri = e.data.payload.invitee.uri

        if (!eventUri || !inviteeUri) {
          console.error('Missing eventUri or inviteeUri')
          return
        }

        // Fetch full event + invitee data from our backend API route
        // (which uses the Calendly PAT to call the Calendly v2 API)
        const response = await fetch('/api/calendly-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventUri, inviteeUri }),
        })

        if (!response.ok) {
          throw new Error(`API call failed: ${response.status}`)
        }

        const data = await response.json()

        const startTimeInTimeZone = new Date(
          data.event.start_time
        ).toLocaleString('en-IN', { timeZone: data.invitee.timezone })

        const endTimeInTimeZone = new Date(data.event.end_time).toLocaleString(
          'en-IN',
          { timeZone: data.invitee.timezone }
        )

        await createSoftLead({
          formData: {
            name: data.invitee.name,
            email: data.invitee.email,
            timezone: data.invitee.timezone,
            startTime: startTimeInTimeZone,
            endTime: endTimeInTimeZone,
            eventGuest: data.event?.event_guests
              ?.map((guest: { email: string }) => guest.email)
              ?.join(','),
          },
          tags: ['BOOK_A_DEMO'],
          message: data.invitee?.questions_and_answers?.at(-1)?.answer,
          pdpCategory: 'BOOK_A_DEMO',
          pdpCategoryId: 'CONTACT_US_FORM',
          deepLink: '',
          pdpId: '',
          pdpTitle: '',
          captchaToken: captchaToken || undefined,
        })
      } catch (error) {
        console.error('Error processing Calendly event:', error)
        show.error({
          title:
            'Meeting scheduled, but there was an issue saving your information. Please contact us directly.',
        })
      }
    },
    [captchaToken, createSoftLead, show]
  )

  return { handleEventSchedule }
}
